import React, { useState } from "react";
import "../App.css";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { useFormik } from "formik";
import * as Yup from "yup";
import { lightAuthTheme, darkAuthTheme } from "../Theme/authTheme";
import { ThemeProvider } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateLoading, updateLoadingMsg } from "../Redux/Reducers/uiSlice";
import { removeUser } from "../Redux/Actions/UserActions";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import { toast } from "react-toastify";
import AuthLogo from "./AuthPages/AuthLogo";
import { useTheme } from "../contexts/ThemeContext";
import { migrateUser } from "../utils/restAPI";

const UsernameMigration = ({ onHideMigration }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [isMigrationComplete, setIsMigrationComplete] = useState(false);

  // Choose the appropriate theme based on current mode
  const authTheme = isDarkMode ? darkAuthTheme : lightAuthTheme;

  // Simple username validation schema
  const usernameValidationSchema = Yup.object({
    username: Yup.string()
      .required("Username is required")
      .min(3, "Username must be at least 3 characters")
      .max(50, "Username must be less than 50 characters")
      .matches(
        /^[a-zA-Z0-9_.]+$/,
        "Username can only contain letters, numbers, underscores, and dots"
      ),
  });

  const initialValues = {
    username: "",
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues: initialValues,
      validationSchema: usernameValidationSchema,
      onSubmit: async ({ username }) => {
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg("Migrating account..."));

        try {
          // Get migration data from localStorage
          const migrationData = JSON.parse(localStorage.getItem("migrationData"));

          if (!migrationData) {
            throw new Error("Migration data not found. Please try logging in again.");
          }

          // Call migration API using the same pattern as other requests
          const migrationResponse = await migrateUser({
            username: username,
            password: migrationData.password,
            token: migrationData.accessToken,
          });

          if (!migrationResponse.success) {
            
            const msg = migrationResponse.message === "Migration failed with status 409" ? "Username already exists. Please choose a different username." : migrationResponse.message;
            throw new Error(msg || "Migration failed");
          }

          // Clear migration data
          localStorage.removeItem("migrationData");
          
          // Clear user state and localStorage
          dispatch(removeUser());

          dispatch(updateLoading(false));

          toast.success("Migration completed successfully! Please log in with your new account.");

          // Set migration complete state to show "Go to Login Page" button
          setIsMigrationComplete(true);

        } catch (err) {
          dispatch(updateLoading(false));

          let errorMessage = "Migration failed. Please try again.";
          if (err.message) {
            errorMessage = err.message;
          }


          toast.error(errorMessage);
        }
      },
    });

  return (
    <ThemeProvider theme={authTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: authTheme.palette.background.default,
          display: "flex",
          zoom: 0.75,
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem 1rem",
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
              borderRadius: 3,
              backgroundColor: authTheme.palette.background.paper,
              boxShadow: authTheme.palette.mode === 'dark'
                ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <AuthLogo />
            </Box>

            {/* Welcome Text */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                  fontWeight: 700,
                  color: authTheme.palette.text.primary,
                  mb: 1,
                }}
              >
                {isMigrationComplete ? "Success!" : "Account Settings"}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: authTheme.palette.text.secondary,
                  fontSize: "1.125rem",
                }}
              >
                {isMigrationComplete
                  ? "Username added! Please click the button below to login and continue using the dashboard."
                  : "Please create a username to complete your account details."}
              </Typography>
            </Box>

            {!isMigrationComplete ? (
              /* Form */
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ mt: 3 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              >
                {/* Username Field */}
                <TextField
                  fullWidth
                  id="username"
                  name="username"
                  label="Username"
                  placeholder="Choose a username"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.username && Boolean(errors.username)}
                  helperText={touched.username && errors.username}
                  sx={{ mb: 3 }}
                />

                {/* Update Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    mb: 3,
                    height: "56px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    backgroundColor: authTheme.palette.mode === 'dark' ? "#F9FAFB" : "#1F2937",
                    color: authTheme.palette.mode === 'dark' ? "#ffffff" : "#FFFFFF",
                    "&:hover": {
                      backgroundColor: authTheme.palette.mode === 'dark' ? "#E5E7EB" : "#374151",
                    },
                  }}
                >
                  Update
                </Button>
              </Box>
            ) : (
              /* Go to Login Page Button */
              <Box sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => {
                    if (onHideMigration) {
                      onHideMigration();
                    }
                    navigate("/login");
                  }}
                  sx={{
                    mb: 3,
                    height: "56px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    backgroundColor: authTheme.palette.mode === 'dark' ? "#F9FAFB" : "#1F2937",
                    color: authTheme.palette.mode === 'dark' ? "#ffffff" : "#FFFFFF",
                    "&:hover": {
                      backgroundColor: authTheme.palette.mode === 'dark' ? "#E5E7EB" : "#374151",
                    },
                  }}
                >
                  Go to Login Page
                </Button>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default UsernameMigration;
